import React, { useMemo } from "react";
import classNames from "classnames";

import AscTable from "../AscTable";
import AscButton from "../../AscButton/AscButton";
import AscAdminAction, {
  AscAdminActionOptionProps,
} from "../../AscAdminAction/AscAdminAction";
import {
  AdminMedal1,
  AdminMedal2,
  AdminMedal3,
  MoreIcon,
} from "../../AscIcons";
import { useOutsideClick } from "@ascendnow/shared";

import styles from "./AscAdminStudentsList.module.scss";
import AscTooltip from "../../AscTooltip/AscTooltip";
import { useAscContext } from "libs/components/src/context";

export interface AscAdminStudentsListProps {
  title?: string;
  studentsData: any;
  batchId?: string;
  option1?: string;
  option2?: string;
  option3?: string;
  clickOption1?: (e?: any) => void;
  clickOption2?: (e?: any) => void;
  clickOption3?: (e?: any) => void;
  onStudentsChange?: (el: any[]) => void;
  onDownload?: (studentId: string) => void;
  onClickEdit?: (obj: any) => void;
  onClickRemove?: (id: string) => void;
  onClickFilter?: () => void;
  noMenu?: boolean;
  pageCount?: number;
  eachPageSize?: number;
}

export enum StudentListEnum {
  id = "id",
  name = "name",
  parentEmail = "parentEmail",
  completion = "completion",
  email = "email",
  schoolRank = "schoolRank",
  globalRank = "globalRank",
  batches = "batches",
  status = "status",
  grade = "grade",
  timeOnPlatform = "timeOnPlatform",
  report = "report",
  action = "action",
  studentId = "studentId",
}

export type FilterFnOfTableT = (
  rows: {
    allCells: any[];
    cells: any[];
    depth: number;
    id: string;
    index: number;
    original: { [key: string]: any };
    values: { [key: string]: any };
  }[],
  columnIds: string[],
  filterValue: any
) => any[];

export interface IMyColumn {
  Header: any;
  accessor: string;
  enableSorting?: boolean;
  Cell?: any;

  filter?: FilterFnOfTableT;
}

export default function AscAdminStudentsList({
  title,
  studentsData,
  option1,
  option2,
  option3,
  clickOption1,
  clickOption2,
  clickOption3,
  onDownload,
  noMenu = false,
  eachPageSize = 10,
  pageCount,
}: AscAdminStudentsListProps) {
  const { toast } = useAscContext();
  const tableData: {
    [StudentListEnum.id]: any;
    [StudentListEnum.name]: string;
    [StudentListEnum.parentEmail]: string;
    [StudentListEnum.completion]: string;
    [StudentListEnum.email]: string;
    [StudentListEnum.schoolRank]: string;
    [StudentListEnum.globalRank]: string;
    [StudentListEnum.batches]: {
      id: string;
      name: string;
    }[];
    [StudentListEnum.status]: string;
    [StudentListEnum.grade]: string;
    [StudentListEnum.timeOnPlatform]: string;
    [StudentListEnum.report]: string;
    [StudentListEnum.action]: string;
    [StudentListEnum.studentId]: string;
  }[] = useMemo(() => [...studentsData], [studentsData]);

  const tableColumns = useMemo(() => {
    const columns = [
      {
        Header: () => {
          return <div className={styles.header}>{"Name"}</div>;
        },
        accessor: StudentListEnum.name,
        enableSorting: true,
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Status"}</div>;
        },
        accessor: StudentListEnum.status,
        enableSorting: true,
        Cell: (cell: { value: any }) => {
          if (cell.value === "Error") {
            return (
              <p className={classNames(styles.status, styles.error)}>Error</p>
            );
          } else if (cell.value === "Active") {
            return (
              <p className={classNames(styles.status, styles.active)}>Active</p>
            );
          } else {
            return (
              <p className={classNames(styles.status, styles.invited)}>
                Invited
              </p>
            );
          }
        },
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Batches"}</div>;
        },
        accessor: StudentListEnum.batches,
        enableSorting: true,
        Cell: (cell: { value: any }) => {
          const output: string[] = [];
          cell?.value?.map((batch: { name: string }) => {
            output.push(batch.name);
          });
          return <div>{output.join(", ")}</div>;
        },
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Email"}</div>;
        },
        accessor: StudentListEnum.email,
        enableSorting: true,
        Cell: (cell: { value: string }) => {
          return (
            <AscTooltip
              tooltipAnchor={
                <div
                  className={styles.email}
                  onDoubleClick={() => {
                    navigator.clipboard.writeText(cell.value);
                    toast.info("Email has been copied to clipboard");
                  }}
                >
                  {cell.value}
                </div>
              }
              tooltipContentClassName={styles.emailTooltip}
            >
              {cell.value}
            </AscTooltip>
          );
        },
      },
      {
        Header: () => {
          return <div className={styles.header}>{"School Rank"}</div>;
        },
        accessor: StudentListEnum.schoolRank,
        enableSorting: true,
        Cell: (cell: { value: number }) => {
          if (cell.value === 1) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal1)}>
                <AdminMedal1 />
              </span>
            );
          } else if (cell.value === 2) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal2)}>
                <AdminMedal2 />
              </span>
            );
          } else if (cell.value === 3) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal3)}>
                <AdminMedal3 />
              </span>
            );
          } else {
            return <span className={styles.rankText}>{cell.value}</span>;
          }
        },
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Global Rank"}</div>;
        },
        accessor: StudentListEnum.globalRank,
        enableSorting: true,
        Cell: (cell: { value: number }) => {
          if (cell.value === 1) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal1)}>
                <AdminMedal1 />
              </span>
            );
          } else if (cell.value === 2) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal2)}>
                <AdminMedal2 />
              </span>
            );
          } else if (cell.value === 3) {
            return (
              <span className={classNames(styles.rankIcon, styles.medal3)}>
                <AdminMedal3 />
              </span>
            );
          } else {
            return <span className={styles.rankText}>{cell.value}</span>;
          }
        },
      },
      {
        Header: () => {
          return <div className={styles.header}>{"Completion"}</div>;
        },
        accessor: StudentListEnum.completion,
        enableSorting: true,
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Grade"}</div>;
        },
        accessor: StudentListEnum.grade,
        enableSorting: true,
        Cell: (cell: { value: number }) =>
          cell.value % 1 === 0 ? cell.value : cell.value.toFixed(2),
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Time on Platform"}</div>;
        },
        accessor: StudentListEnum.timeOnPlatform,
        enableSorting: true,
      },

      {
        Header: () => {
          return <div className={styles.header}>{"Report"}</div>;
        },
        accessor: StudentListEnum.studentId,

        Cell: (cell: {
          value: string;
          row: { values: { status: "Invited" | "Active" } };
        }) => (
          <AscButton
            onClick={() => onDownload?.(cell.value)}
            className={styles.button}
            size="small"
            disabled={cell?.row?.values?.status === "Invited"}
          >
            View
          </AscButton>
        ),
      },

      {
        Header: () => {
          return !noMenu ? <div className={styles.header}>{"Action"}</div> : "";
        },
        accessor: StudentListEnum.action,

        Cell: (props: any) => {
          const { id } = props.cell.row.original;

          return !noMenu ? (
            <Action
              menuOptions={[
                {
                  title: option1 || "",
                  onClick: () => clickOption1?.(props.cell.row.original),
                },
                {
                  title: option2 || "",
                  onClick: () => clickOption2?.(id),
                },
                {
                  title: option3 || "",
                  onClick: () => clickOption3?.(props.cell.row.original),
                },
              ]}
            />
          ) : (
            <></>
          );
        },
      },
    ];

    return columns;

    //eslint-disable-next-line
  }, []);

  return (
    <div className={styles.table}>
      <span className={styles.title}>{title}</span>

      <AscTable
        tHead={styles.tHead}
        myTable={styles.myTable}
        tHeadRow={styles.tHeadRow}
        theadCol={styles.tHeadCol}
        tH={styles.tH}
        tBody={styles.tBody}
        tRow={styles.tRow}
        pureCellFlow={styles.pureCellFlow}
        tD={styles.tD}
        tBodyValue={styles.tBodyValue}
        tableColumns={tableColumns}
        tableData={tableData}
        showHeader={true}
        eachPageSize={eachPageSize}
        totalPageCount={pageCount}
        className={styles.tableWrapper}
      />
    </div>
  );
}

const Action = ({
  menuOptions,
}: {
  menuOptions: AscAdminActionOptionProps[];
}) => {
  const { ref, isVisible, setIsVisible } =
    useOutsideClick<HTMLDivElement>(false);

  const handleClick = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div ref={ref}>
      <div onClick={handleClick}>
        <MoreIcon className={styles.moreIcon} />
      </div>

      {isVisible && (
        <AscAdminAction className={styles.action} options={menuOptions} />
      )}
    </div>
  );
};
